import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, Phone, MessageSquare, ExternalLink } from "lucide-react";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  useDocumentMeta("fr", { noindex: true });
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.get(`${API}/consultations`, {
        headers: { "X-Admin-Password": password },
      });
      setRows(res.data);
      setAuthed(true);
      toast.success(`${res.data.length} consultation(s)`);
    } catch (err) {
      toast.error("Mot de passe invalide");
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#080B11] text-white flex items-center justify-center p-6">
        <form
          onSubmit={login}
          className="relative bg-[#131824] border border-white/10 w-full max-w-md p-10"
          data-testid="admin-login-form"
        >
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#B81D33] to-transparent" />
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] font-bold mb-3">
            Marocanada · Admin
          </div>
          <h1 className="font-serif-display text-3xl text-white mb-2">
            Accès restreint
          </h1>
          <p className="text-sm text-white/55 mb-8">
            Tableau des consultations.
          </p>
          <label className="text-[10px] tracking-[0.3em] uppercase text-white/55 mb-2 block">
            Mot de passe
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-0 top-1/2 -translate-y-1/2 text-white/40" strokeWidth={1.4} />
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-[#D4AF37] text-white py-3 pl-6 outline-none text-sm"
              required
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="mt-8 w-full bg-[#B81D33] hover:bg-[#8f1526] disabled:opacity-60 text-white py-3.5 rounded-sm tracking-wide text-sm transition-colors"
          >
            {loading ? "…" : "Entrer"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080B11] text-white">
      <div className="border-b border-white/10 bg-[#080B11]/90 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] font-bold">
              Marocanada · Admin
            </div>
            <h1 className="font-serif-display text-2xl text-white mt-1">
              Consultations ({rows.length})
            </h1>
          </div>
          <a
            href="/"
            data-testid="admin-back-home"
            className="text-xs tracking-[0.25em] uppercase text-white/55 hover:text-white"
          >
            ← Retour
          </a>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {rows.length === 0 ? (
          <div className="text-center text-white/55 py-20" data-testid="admin-empty">
            Aucune consultation pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {rows.map((r) => (
              <article
                key={r.id}
                data-testid={`admin-row-${r.id}`}
                className="bg-[#131824] border border-white/10 border-l-2 border-l-[#B81D33] p-6"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-serif-display text-xl text-white">
                      {r.full_name}
                    </h3>
                    <div className="text-xs text-white/45 mt-1">
                      {new Date(r.created_at).toLocaleString("fr-FR")} · {r.language?.toUpperCase()}
                    </div>
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1">
                    {r.service}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <a
                    href={`mailto:${r.email}`}
                    className="flex items-center gap-2 text-white/75 hover:text-white"
                  >
                    <Mail className="w-3.5 h-3.5 text-[#B81D33]" strokeWidth={1.4} />
                    {r.email}
                    <ExternalLink className="w-3 h-3 text-white/30" />
                  </a>
                  {r.phone && (
                    <a
                      href={`https://wa.me/${r.phone.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-white/75 hover:text-white"
                    >
                      <Phone className="w-3.5 h-3.5 text-[#B81D33]" strokeWidth={1.4} />
                      {r.phone}
                    </a>
                  )}
                  {r.message && (
                    <div className="flex items-start gap-2 text-white/65 mt-3 pt-3 border-t border-white/8">
                      <MessageSquare className="w-3.5 h-3.5 text-[#B81D33] mt-0.5 flex-shrink-0" strokeWidth={1.4} />
                      <p className="leading-relaxed">{r.message}</p>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
